import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Coins,
  Zap,
  ArrowUpRight,
  TrendingUp,
  Calendar,
  History,
  CheckCircle2,
} from "lucide-react";
import { useCreditSystem } from "@/hooks/useCreditSystem";
import { useSubscription } from "@/hooks/useSubscription";
import { UserCreditUsage } from "@/components/settings/UserCreditUsage";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const topupPackages = [
  { name: "Mini", credits: 100, price: 5, discount: null, popular: false, best: false },
  { name: "Standard", credits: 500, price: 18, discount: "10% off", popular: true, best: false },
  { name: "Power", credits: 2000, price: 55, discount: "30% off", popular: false, best: true },
  { name: "Agency", credits: 10000, price: 220, discount: "45% off", popular: false, best: false },
];

export function CreditsSection() {
  const { stats, isLoading } = useCreditSystem();
  const { subscription } = useSubscription();
  const { toast } = useToast();
  const [purchasing, setPurchasing] = useState<string | null>(null);

  const balance = stats.currentBalance ?? subscription?.credits_remaining ?? 0;
  const monthly = subscription?.credits_monthly ?? 0;
  const progress = monthly > 0 ? Math.min((balance / monthly) * 100, 100) : 0;

  const canTopup =
    subscription &&
    subscription.tier !== "welcome" &&
    (subscription.status === "active" ||
      (subscription.status === "cancelled" &&
        subscription.current_period_end &&
        new Date(subscription.current_period_end) > new Date()));

  const handleBuyTopup = async (pkg: (typeof topupPackages)[0]) => {
    setPurchasing(pkg.name);
    try {
      const { data, error } = await supabase.functions.invoke("create-topup-payment", {
        body: { package: pkg.name.toLowerCase() },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast({
        title: "Purchase failed",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
      setPurchasing(null);
    }
  };

  return (
    <div className="space-y-7">
      {/* Balance hero */}
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary/15 via-card to-card p-6 sm:p-8">
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

        <div className="relative">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2 px-2 py-1 rounded-full bg-muted/40">
                <Coins className="h-3 w-3" />
                Current balance
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl sm:text-6xl font-bold tracking-tight tabular-nums bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
                  {isLoading ? "—" : Math.round(balance).toLocaleString()}
                </span>
                <span className="text-muted-foreground text-sm">credits</span>
              </div>
            </div>

            {monthly > 0 && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">of {monthly.toLocaleString()} monthly</p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5">{Math.round(progress)}%</p>
                <p className="text-[11px] text-muted-foreground">remaining</p>
              </div>
            )}
          </div>

          {monthly > 0 && (
            <div className="space-y-1.5">
              <Progress value={progress} className="h-2" />
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>0</span>
                <span>{monthly.toLocaleString()}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-border/40">
            <Stat label="Last 7 days" value={Math.round(stats.usedLast7Days)} />
            <Stat label="This period" value={Math.round(stats.usedThisPeriod)} />
            <Stat label="All time" value={Math.round(stats.totalUsed)} />
          </div>
        </div>
      </div>

      {/* Top-ups */}
      {canTopup ? (
        <section id="topup-section">
          <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Zap className="h-4 w-4" />
                </div>
                <h2 className="font-semibold text-base">Buy top-up credits</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Top-ups are added immediately and reset to your monthly allowance at the next renewal.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {topupPackages.map((pkg) => (
              <div
                key={pkg.name}
                className={cn(
                  "relative flex flex-col rounded-xl border bg-card p-5 transition-all hover:shadow-fluent-8 hover:-translate-y-0.5",
                  pkg.best ? "border-primary/40 ring-1 ring-primary/20 shadow-fluent-4" : "border-border/60"
                )}
              >
                {pkg.best && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary shadow-fluent-2 text-[10px] uppercase tracking-wider px-2 py-0.5">
                      Best value
                    </Badge>
                  </div>
                )}
                {pkg.popular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <Badge variant="secondary" className="text-[10px] uppercase tracking-wider px-2 py-0.5">
                      Popular
                    </Badge>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-sm font-semibold text-muted-foreground">{pkg.name}</h3>
                  <div className="mt-2 flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold tracking-tight">€{pkg.price}</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-1.5 text-xs text-muted-foreground">
                    <Coins className="h-3 w-3" />
                    {pkg.credits.toLocaleString()} credits
                  </div>
                  {pkg.discount && (
                    <div className="inline-flex items-center gap-1 mt-2 text-[11px] text-success font-medium px-1.5 py-0.5 rounded bg-success/10">
                      <CheckCircle2 className="h-3 w-3" />
                      {pkg.discount}
                    </div>
                  )}
                </div>

                <Button
                  className="w-full mt-5"
                  size="sm"
                  variant={pkg.best ? "default" : "outline"}
                  onClick={() => handleBuyTopup(pkg)}
                  disabled={purchasing !== null}
                >
                  {purchasing === pkg.name ? "Processing…" : "Buy now"}
                </Button>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <div className="rounded-xl border border-dashed border-border/80 bg-card p-8 text-center">
          <div className="mx-auto h-12 w-12 rounded-xl bg-muted flex items-center justify-center mb-3">
            <TrendingUp className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="font-semibold mb-1">Top-ups need an active subscription</h3>
          <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
            Choose a plan to unlock top-up purchases and additional features.
          </p>
          <Button asChild>
            <Link to="/plans" className="gap-2">
              Browse plans <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}

      {/* Transaction history */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center">
            <History className="h-4 w-4 text-muted-foreground" />
          </div>
          <h2 className="font-semibold text-base">Transaction history</h2>
        </div>
        <UserCreditUsage />
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Calendar className="h-2.5 w-2.5" />
        {label}
      </div>
      <p className="text-xl font-semibold tabular-nums mt-1">{value.toLocaleString()}</p>
    </div>
  );
}
